/**
 * Rich PDP Content snippet-author runtime.
 *
 * Sections are inline in the slot payload JSON (entry.content.sections).
 * SSR emits the shell (heading + empty sections container). This JS
 * renders the full section content from the envelope on bind + variant swap.
 */
;(() => {
  if (window.__sai_dm8uy32e_initialized__) return
  window.__sai_dm8uy32e_initialized__ = true

  const SNIPPET_ID = 'dm8uy32e'

  // Newest rich-sections CSS payload shape this runtime understands.
  // cssVersion 2 = shared-CSS extraction (content.sharedCss lifted out of
  // per-section styles). A payload stamped newer than this means the theme's
  // installed asset predates the save that wrote it.
  const SUPPORTED_CSS_VERSION = 2

  function readEnvelope(node) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]
      if (child.tagName === 'SCRIPT' && child.hasAttribute('data-spectrum-envelope')) {
        try {
          return JSON.parse(child.textContent || '')
        } catch {
          return null
        }
      }
    }
    return null
  }

  // Resolve the entry the visitor actually sees: the resolver-picked entry
  // when present, else the slot's default. Sections, sectionHandles, and
  // motion all read from this one entry — draft preview needs no special
  // casing here because the wrapper SSRs the draft metafield's envelope in
  // preview, so whatever entry resolves IS the draft entry.
  function getResolvedEntry(envelope, variant, entry) {
    const slotKey = variant?.meta?.contentSlot?.slotKey || 'default'
    const slot = envelope?.slotsByKey?.[slotKey]
    if (!slot) return null
    return entry || slot.entries?.find((e) => e.isDefault) || slot.entries?.[0] || null
  }

  const cleanupByRoot = new WeakMap()

  function registerCleanup(root, cleanup) {
    const cleanups = cleanupByRoot.get(root) || []
    cleanups.push(cleanup)
    cleanupByRoot.set(root, cleanups)
  }

  function cleanupEnhancedSections(root) {
    const cleanups = cleanupByRoot.get(root) || []
    for (const cleanup of cleanups.splice(0)) {
      try {
        cleanup()
      } catch {}
    }
    cleanupByRoot.delete(root)
  }

  // ── Cross-block motion (scenes) ─────────────────────────────────────
  // The snippet's motion surface is deliberately thin: build the host config
  // for the shared loader (window.SpectrumMotion, a theme asset installed by
  // the runtime-infra channel), register teardown into the cleanup registry,
  // and suppress legacy per-section scroll inits for scene-claimed sections
  // (R28). Scene DOM grouping, tiering, the kill switch, and the payload
  // version gate all live in the loader/engine — the payload passes through
  // untouched (no classifyMotionVersion here; a payload newer than the
  // installed runtime renders static via the loader's own gate).

  function hasMotionScenes(motion) {
    return (
      !!motion &&
      typeof motion === 'object' &&
      Array.isArray(motion.scenes) &&
      motion.scenes.length > 0
    )
  }

  // Mirrors resolveEffectiveSectionIds in the rich-sections write path:
  // segment entries may carry sectionHandles (an ordered subset/reorder of
  // their own sections); the rendered list is exactly that when present.
  // Motion block ids are the stable sectionIds of the rendered sections, in
  // render order — sections without a sectionId contribute no block.
  function computeEffectiveSectionIds(sections, sectionHandles) {
    const byHandle = {}
    for (const s of sections) {
      if (s.handle) byHandle[s.handle] = s
    }
    const handles = sectionHandles?.length ? sectionHandles : sections.map((s) => s.handle)
    const ids = []
    for (const handle of handles) {
      const sectionId = byHandle[handle]?.sectionId
      if (typeof sectionId === 'string' && sectionId) ids.push(sectionId)
    }
    return ids
  }

  // HAND-MIRRORED from @spectrum/motion's resolveScenes claim semantics (the
  // canonical R28 claim predicate) — this standalone theme asset cannot
  // import the package. Mirror ONLY what the predicate needs: a scene claims
  // its members iff it has ≥2 members, every member resolves in the ordered
  // block list, the members form a consecutive run, and no member is already
  // claimed (first claim in doc order wins). Pinned by the shared
  // claim-predicate contract scenarios in @spectrum/testing/motion-contracts
  // — any divergence from the canonical implementation fails those tests,
  // not production.
  function computeClaimedBlockIds(orderedBlockIds, motion) {
    const claimed = new Set()
    if (!hasMotionScenes(motion)) return claimed
    for (const scene of motion.scenes) {
      const blockIds = scene && Array.isArray(scene.blockIds) ? scene.blockIds : []
      if (blockIds.length < 2) continue
      const indices = []
      let missing = false
      for (const blockId of blockIds) {
        const index = orderedBlockIds.indexOf(blockId)
        if (index === -1) {
          missing = true
          break
        }
        indices.push(index)
      }
      if (missing) continue
      indices.sort((a, b) => a - b)
      if (indices[indices.length - 1] - indices[0] + 1 !== indices.length) continue
      if (blockIds.some((id) => claimed.has(id))) continue
      for (const id of blockIds) claimed.add(id)
    }
    return claimed
  }

  // R28 consult point for legacy per-section scroll inits: a module inside a
  // scene-claimed section root (data-aplus-section-id) must not start its
  // legacy scroll behavior — the scene owns that section's motion.
  function isModuleClaimedByScene(module, claimedBlockIds) {
    if (!claimedBlockIds || claimedBlockIds.size === 0) return false
    const sectionRoot = module.closest('[data-aplus-section-id]')
    const blockId = sectionRoot?.getAttribute('data-aplus-section-id')
    return !!blockId && claimedBlockIds.has(blockId)
  }

  // Loader script injection, deduped across snippet instances via a window
  // key (same handshake the loader itself uses for the engine chunk).
  function loadMotionLoader(loaderUrl) {
    if (window.SpectrumMotion?.initMotion) return Promise.resolve(window.SpectrumMotion)
    if (!window.__saiMotionLoaderLoading) {
      window.__saiMotionLoaderLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = loaderUrl
        script.async = true
        script.onload = () => {
          if (window.SpectrumMotion?.initMotion) resolve(window.SpectrumMotion)
          else reject(new Error('motion loader global missing after script load'))
        }
        script.onerror = () => {
          reject(new Error(`motion loader script failed to load: ${loaderUrl}`))
        }
        document.head.appendChild(script)
      })
    }
    return window.__saiMotionLoaderLoading
  }

  // Kill switch (R20): operator flag delivered through the app-embed config
  // global. Absent config means motion is enabled — the flag is an explicit
  // disable, not an allowlist.
  function motionKillSwitch() {
    const config = window.__spectrumAi?.motion?.config
    return !!config && config.disabled === true
  }

  // Map engine telemetry onto the snippet's analytics channel. Event names
  // follow the snippet convention `<feature_slug>:<event_name>`; payload keys
  // are snake_case. `event.attribution` is the bag this asset passed at init
  // (surface / entry_id / variant_id) — spread so every event stays
  // attributable to the served entry (R24).
  function motionTelemetryToTrack(event) {
    const base = event.attribution || {}
    switch (event.type) {
      case 'tier-served':
        return {
          name: 'rich_sections:motion_tier_served',
          payload: { ...base, tier: event.tier, reasons: event.reasons, source: event.source },
        }
      case 'scene-enter':
        return {
          name: 'rich_sections:motion_scene_enter',
          payload: { ...base, scene_id: event.sceneId },
        }
      case 'scene-complete':
        return {
          name: 'rich_sections:motion_scene_complete',
          payload: { ...base, scene_id: event.sceneId },
        }
      case 'scene-max-progress':
        return {
          name: 'rich_sections:motion_scene_max_progress',
          payload: { ...base, scene_id: event.sceneId, max_progress: event.maxProgress },
        }
      case 'engine-load-failed':
        return {
          name: 'rich_sections:motion_engine_load_failed',
          payload: { ...base, reason: event.reason },
        }
      case 'version-gate':
        return {
          name: 'rich_sections:motion_version_gate',
          payload: {
            ...base,
            payload_version: event.payloadVersion,
            runtime_version: event.runtimeVersion,
          },
        }
      default:
        return null
    }
  }

  // Initialize motion for the resolved entry. Teardown registers into the
  // same cleanup registry as the legacy enhancements, so a variant swap
  // (bind callback re-fires → renderSections → cleanupEnhancedSections)
  // fully destroys the running engine — triggers killed, pin spacers
  // removed, DOM restored — before the new entry renders and re-inits. A
  // swap that lands while the loader script is still in flight flips the
  // destroyed flag so the late init is skipped instead of orphaned.
  function initSectionsMotion(args) {
    const motion = args.entry?.content?.motion
    if (!hasMotionScenes(motion)) return

    const track = args.track
    const assets = window.__spectrumMotionAssets
    if (!assets || typeof assets.loaderUrl !== 'string' || !assets.loaderUrl) {
      console.warn(
        '[sai-dm8uy32e] motion declared but window.__spectrumMotionAssets is missing — runtime-infra install is stale; rendering static',
      )
      track?.('rich_sections:motion_loader_unavailable', { reason: 'asset_urls_missing' })
      return
    }

    const attribution = { surface: 'rich_pdp' }
    if (args.entry?.entryId) attribution.entry_id = String(args.entry.entryId)
    if (args.variant?.variantId) attribution.variant_id = String(args.variant.variantId)

    const state = { destroyed: false, handle: null }
    registerCleanup(args.sectionsEl, () => {
      state.destroyed = true
      state.handle?.destroy()
      state.handle = null
    })

    loadMotionLoader(assets.loaderUrl)
      .then((SpectrumMotion) => {
        if (state.destroyed) return
        state.handle = SpectrumMotion.initMotion({
          motion,
          root: args.sectionsEl,
          engineUrl: typeof assets.engineUrl === 'string' ? assets.engineUrl : undefined,
          killSwitch: motionKillSwitch,
          orderedBlockIds: args.orderedBlockIds,
          blockIdAttributes: ['data-aplus-section-id'],
          attribution,
          telemetrySink: (event) => {
            const mapped = motionTelemetryToTrack(event)
            if (mapped) track?.(mapped.name, mapped.payload)
          },
          probeCacheKey:
            window.Shopify?.theme?.id != null ? String(window.Shopify.theme.id) : undefined,
        })
        // Swap raced the loader fetch: destroy immediately, never run.
        if (state.destroyed) {
          state.handle.destroy()
          state.handle = null
        }
      })
      .catch((err) => {
        console.warn('[sai-dm8uy32e] motion loader failed to load; rendering static', err)
        track?.('rich_sections:motion_loader_unavailable', {
          reason: 'loader_load_failed',
          detail: String(err),
        })
      })
  }

  function appendUnavailableSection(parent, handle) {
    const section = document.createElement('div')
    section.className = 'sai-dm8uy32e__section sai-dm8uy32e__section--unavailable'
    section.setAttribute('aria-hidden', 'true')
    if (handle) section.dataset.handle = String(handle)
    parent.append(section)
  }

  // Scripts inserted via innerHTML or cloneNode are inert per the HTML spec.
  // Custom HTML sections need merchant <script> tags (analytics pixels,
  // third-party embeds, inline init code) to actually run — walk those
  // section wrappers after insertion and swap each script for a freshly
  // created element so the parser activates it. Scoped to custom-section
  // wrappers only: every other section type is sanitized server-side and
  // shouldn't contain executable scripts; broadening the walk risks
  // re-executing legitimate JSON-LD or third-party blocks on every
  // re-render (variant change, navigation rebind, etc.).
  function executeInlineScripts(root) {
    const wrappers = root.querySelectorAll('[data-section-type="custom"]')
    for (const wrapper of wrappers) {
      const scripts = wrapper.querySelectorAll('script')
      for (const oldScript of scripts) {
        const newScript = document.createElement('script')
        for (const attr of oldScript.attributes) {
          newScript.setAttribute(attr.name, attr.value)
        }
        if (oldScript.textContent) newScript.textContent = oldScript.textContent
        oldScript.replaceWith(newScript)
      }
    }
  }

  function renderSections(sectionsEl, sectionHandles, sections, sharedCss) {
    cleanupEnhancedSections(sectionsEl)

    const byHandle = {}
    for (const s of sections) {
      if (s.handle) byHandle[s.handle] = s
    }

    const handles = sectionHandles?.length ? sectionHandles : sections.map((s) => s.handle)
    const fragment = document.createDocumentFragment()

    // Shared stylesheet: rules the compiler lifted out of two-or-more sections
    // at save time. Must land before every section <style> — extracted
    // sections no longer carry these rules themselves. Absent on envelopes
    // saved before extraction existed (sections then render standalone).
    if (sharedCss) {
      const shared = document.createElement('style')
      shared.setAttribute('data-sai-shared-css', '')
      shared.textContent = String(sharedCss)
      fragment.append(shared)
    }

    for (const handle of handles) {
      const section = byHandle[handle]
      if (!section?.html) {
        appendUnavailableSection(fragment, handle)
        continue
      }

      const wrapper = document.createElement('div')
      wrapper.className = 'sai-dm8uy32e__section'
      if (section.sectionType) wrapper.dataset.sectionType = String(section.sectionType)
      if (handle) wrapper.dataset.handle = String(handle)

      if (section.css) {
        const style = document.createElement('style')
        style.textContent = String(section.css)
        wrapper.append(style)
      }

      const template = document.createElement('template')
      template.innerHTML = String(section.html)
      wrapper.append(template.content.cloneNode(true))
      fragment.append(wrapper)
    }
    sectionsEl.replaceChildren(fragment)
    executeInlineScripts(sectionsEl)
  }

  // The enhancement runtime lives in its own theme asset
  // (spectrum-aplus-runtime.js), installed through the runtime-infra channel
  // and exposing window.SpectrumAplusRuntime.enhance. It may load after this
  // script or be absent entirely on a theme whose infra assets predate it —
  // poll briefly, then render static. Never throw on absence: the sections
  // stay readable, only the interactive enhancements are missing.
  function runEnhance(sectionsEl, claimedBlockIds) {
    const register = (cleanup) => registerCleanup(sectionsEl, cleanup)
    let attempts = 0
    let timer = null
    // A re-render tears pending polls down through the same cleanup registry
    // (cleanupEnhancedSections runs before every render), so a late-loading
    // runtime can never enhance a swapped-out render with stale claim data.
    register(() => {
      if (timer) window.clearTimeout(timer)
    })
    const tryRun = () => {
      timer = null
      const runtime = window.SpectrumAplusRuntime
      if (runtime && typeof runtime.enhance === 'function') {
        runtime.enhance(sectionsEl, { register, claimedBlockIds })
        return
      }
      if (attempts++ >= 10) {
        console.warn(
          '[sai-dm8uy32e] enhancement runtime asset missing — theme assets are stale; republish to refresh. Sections render static',
        )
        return
      }
      timer = window.setTimeout(tryRun, 500)
    }
    tryRun()
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__) {
    globalThis.__saiDm8uy32e = {
      getResolvedEntry,
      hasMotionScenes,
      computeEffectiveSectionIds,
      computeClaimedBlockIds,
      isModuleClaimedByScene,
      motionTelemetryToTrack,
      initSectionsMotion,
      renderSections,
      cleanupEnhancedSections,
    }
  }

  const bind = window.__spectrumAi?.snippet?.bind
  if (typeof bind !== 'function') return

  const containers = document.querySelectorAll(`[data-spectrum-snippet-id="${SNIPPET_ID}"]`)
  for (const node of containers) {
    // bind() may invoke the callback synchronously (race-safe replay) before
    // `handles` is assigned — buffer events fired in that window and flush
    // them once the bind handle exists, so no beacon is silently dropped.
    let handles = null
    const pendingTrack = []
    const track = (name, payload) => {
      if (handles) handles.track(name, payload)
      else pendingTrack.push([name, payload])
    }
    handles = bind(node, (ctx) => {
      if (node.getAttribute('data-spectrum-vis') === 'off') return

      const envelope = readEnvelope(node)
      if (!envelope) return

      const inner = node.querySelector('.sai-dm8uy32e')
      if (!inner) return

      const variant = ctx.variant
      const entry = getResolvedEntry(envelope, variant, ctx.entry)
      const sections = entry?.content?.sections || []
      const sectionHandles = entry?.content?.sectionHandles
      const sharedCss = entry?.content?.sharedCss
      const cssVersion = entry?.content?.cssVersion
      if (typeof cssVersion === 'number' && cssVersion > SUPPORTED_CSS_VERSION) {
        console.warn(
          `[sai-dm8uy32e] rich-sections payload cssVersion ${cssVersion} is newer than the installed runtime (supports ${SUPPORTED_CSS_VERSION}) — theme assets are stale; republish to refresh. Sections may render unstyled`,
        )
      }

      const sectionsEl = inner.querySelector('.sai-dm8uy32e__sections')
      if (sectionsEl && sections.length > 0) {
        // renderSections runs cleanupEnhancedSections first — on a variant
        // swap that tears down the previous entry's motion engine (pins,
        // spacers, triggers) before the new DOM lands.
        renderSections(sectionsEl, sectionHandles, sections, sharedCss)
        const orderedBlockIds = computeEffectiveSectionIds(sections, sectionHandles)
        const claimedBlockIds = computeClaimedBlockIds(orderedBlockIds, entry?.content?.motion)
        runEnhance(sectionsEl, claimedBlockIds)
        initSectionsMotion({ sectionsEl, entry, variant, orderedBlockIds, track })
      }
    })
    for (const [name, payload] of pendingTrack.splice(0)) {
      handles.track(name, payload)
    }
  }
})()